import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Button } from '@shared/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { BatchApiAdapter } from '../adapters/BatchApiAdapter';
import { getBatchTotalStock } from '../models/Batch';

interface BatchSelectProps {
  productId: number | null;
  value: number | null;
  onChange: (batchId: number | null) => void;
  disabled?: boolean;
  alwaysVisible?: boolean;
  onCreateClick?: () => void;
  createLabel?: string;
  emptyLabel?: string;
}

const formatDate = (date: string | null): string => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

export const BatchSelect = ({
  productId,
  value,
  onChange,
  disabled,
  alwaysVisible = false,
  onCreateClick,
  createLabel,
  emptyLabel,
}: BatchSelectProps) => {
  const { t } = useTranslation('batchesModule');

  const httpClient = createHttpClient(apiClientWH);
  const adapter = new BatchApiAdapter(httpClient);

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ['product-batches-select', productId],
    queryFn: () => adapter.getProductBatches(productId!),
    enabled: !!productId,
    staleTime: 2 * 60 * 1000,
  });

  const activeBatches = batches.filter((b) => !b.deleted_at && !(b.blocked_at && !b.unblocked_at));

  if (!productId) return null;
  if (!alwaysVisible && activeBatches.length === 0 && !isLoading) return null;

  if (alwaysVisible && !isLoading && activeBatches.length === 0) {
    return (
      <div>
        <label className="block text-sm mb-2 text-text">{t('select.label')}</label>
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 space-y-3">
          <p className="text-sm text-muted-foreground">
            {emptyLabel || t('select.empty', 'Este producto no tiene lotes registrados.')}
          </p>
          {onCreateClick && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCreateClick}
              disabled={disabled}
              data-testid="batch-select-create-button"
            >
              {createLabel || t('select.create', 'Registrar lote')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="block text-sm text-text">{t('select.label')}</label>
        {onCreateClick && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs"
            onClick={onCreateClick}
            disabled={disabled || isLoading}
            data-testid="batch-select-create-button"
          >
            {createLabel || t('select.create', 'Registrar lote')}
          </Button>
        )}
      </div>
      <Select
        value={value?.toString() ?? ''}
        onValueChange={(v) => onChange(v ? Number(v) : null)}
        disabled={disabled || isLoading}
      >
        <SelectTrigger>
          <SelectValue placeholder={isLoading ? t('select.loading') : t('select.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {activeBatches.map((batch) => {
            const stock = getBatchTotalStock(batch);
            return (
              <SelectItem key={batch.id} value={batch.id.toString()}>
                {batch.batch_number}
                {batch.expiry_date ? ` — ${formatDate(batch.expiry_date)}` : ''}
                {` (${stock} uds.)`}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
